/* =====================================================================
 * GuideArticle — 사용자 가이드 한 편을 문서 형태로 그린다.
 *
 * 구성: 경로 → 제목 → 업데이트 날짜 → 도입 문단 → 이 문서의 목차 → 본문
 * → 이전/다음 문서. 폭이 넉넉할 때만 오른쪽에 현재 위치를 따라가는 목차.
 *
 * 본문 표기 약속 (docs/*.md):
 *   - `![대체 텍스트](images/agent/x.png "캡션")` — 캡처. 캡션은 title 자리.
 *     캡처는 device scale 2 로 찍고 가장자리 여백을 잘라 둔다 (본문 1.5x 밀도, 누르면 원본 확대).
 *     같은 표기로 `.mp4` 를 걸면 자동 재생 · 반복 · 음소거 영상으로 그린다(노션 도움말처럼
 *     메뉴를 옮겨 다니는 조작을 보여줄 때).
 *   - `> [!NOTE]` / `> [!TIP]` / `> [!WARNING]` — 안내 박스.
 *   - `` `Enter` `` — 인라인 코드는 키·버튼 표기로 그린다.
 *   - `1. **라벨** — 설명` — 캡처 속 번호 표시를 풀이하는 범례. 파란 번호로 그린다.
 *     그 밖의 번호 목록은 따라 하는 단계라 차분한 회색 번호로 그린다.
 *   - `### …?` — 물음표로 끝나는 소제목은 펼치고 접는 FAQ 카드가 된다.
 * ===================================================================== */

import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toText } from 'hast-util-to-text';
import type { Element } from 'hast';
import { Check, ChevronDown, Info, Lightbulb, Link2, Maximize2, TriangleAlert } from 'lucide-react';

import { ImageLightbox, type ImageLightboxItem } from './ImageLightbox';
import { copyTextToClipboard } from '../../lib/clipboard';
import { guideMediaSize, resolveGuideImage } from './docs';
import { buildGuideOutline, plainHeadingText } from './guideOutline';
import { settleOnGuideHeading } from './useActiveHeading';
import { rehypeGuideCallout, type GuideCalloutKind } from './rehypeGuideCallout';
import { rehypeGuideFaq } from './rehypeGuideFaq';
import { remarkGuideBreaks } from './remarkGuideBreaks';
import * as S from './GuideArticle.styles';
import { GuideSiblings, type GuideSibling } from './GuideSiblings';
import { GuideScrollTop } from './GuideScrollTop';

const CALLOUT: Record<GuideCalloutKind, { label: string; Icon: typeof Info }> = {
  note: { label: '참고', Icon: Info },
  tip: { label: '팁', Icon: Lightbulb },
  warning: { label: '주의', Icon: TriangleAlert },
};

const REMARK_PLUGINS = [remarkGfm, remarkGuideBreaks];
const REHYPE_PLUGINS = [rehypeGuideCallout, rehypeGuideFaq];
/* 도입부에도 `> [!NOTE]` 안내 박스를 쓴다. FAQ 는 본문 절에만 있으므로 빼 둔다. */
const INTRO_REHYPE_PLUGINS = [rehypeGuideCallout];

export interface GuideArticleProps {
  readonly markdown: string;
  readonly title: string;
  /** 경로 표시에 쓰는 상위 묶음 이름 (예: 워크스페이스). */
  readonly section?: string;
  readonly guideTitle: string;
  readonly updatedAt?: string;
  readonly tocOpen: boolean;
  /** 본문을 감싸는 스크롤 컨테이너. 목차 위치 추적 기준. */
  readonly scrollRootRef: React.RefObject<HTMLElement>;
  /** 지금 읽고 있는 절 id. 왼쪽 목차와 같은 값을 쓰도록 페이지가 계산해 내려준다. */
  readonly activeHeadingId: string | null;
  readonly prev?: GuideSibling;
  readonly next?: GuideSibling;
  /** 절 id → 공유용 주소. 없으면 절 제목의 링크 복사 버튼을 그리지 않는다. */
  readonly sectionLink?: (headingId: string) => string;
}

function formatUpdatedAt(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return `${year}년 ${month}월 ${day}일 업데이트`;
}

/** `1. **라벨** — 설명` 형태만 모인 번호 목록인지 — 캡처 번호 범례로 그린다. */
function isLegendList(node: Element | undefined): boolean {
  if (!node) return false;
  const items = node.children.filter((child): child is Element => child.type === 'element' && child.tagName === 'li');
  return (
    items.length > 0 &&
    items.every((item) => {
      const [first, second] = item.children.filter((c) => !(c.type === 'text' && c.value.trim() === ''));
      return (
        first?.type === 'element' &&
        first.tagName === 'strong' &&
        second?.type === 'text' &&
        /^\s*—/.test(second.value)
      );
    })
  );
}

/* 절 제목 옆 링크 복사. 복사 후 잠깐 체크 표시로 바뀐다. */
const SectionAnchor: React.FC<{ link: string; label: string }> = ({ link, label }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <S.AnchorButton
      type="button"
      aria-label={copied ? '링크를 복사했어요' : `${label} 링크 복사`}
      data-copied={copied || undefined}
      onClick={async () => {
        if (await copyTextToClipboard(link)) setCopied(true);
      }}
    >
      {copied ? <Check size={14} /> : <Link2 size={14} />}
      <S.AnchorTip aria-hidden>{copied ? '복사됨' : '링크 복사'}</S.AnchorTip>
    </S.AnchorButton>
  );
};

const isVideo = (src: string | undefined) => Boolean(src && /\.mp4$/i.test(src));

/* 조작 영상. 영상도 2배 해상도로 녹화하므로 불러온 뒤 가로를 1.5로 나눠 캡처와 같은
 * 비율로 보이게 한다. 동작을 줄이는 설정을 켠 사용자에게는 자동 재생하지 않는다. */
const GuideVideo: React.FC<{ src: string; label: string; size?: readonly [number, number] }> = ({ src, label, size }) => {
  const [width, setWidth] = useState<number | undefined>(size ? size[0] / 1.5 : undefined);
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return (
    <video
      src={src}
      aria-label={label}
      autoPlay={!reduceMotion}
      controls={reduceMotion}
      loop
      muted
      playsInline
      preload="metadata"
      style={width ? { width, aspectRatio: size ? `${size[0]} / ${size[1]}` : undefined } : undefined}
      onLoadedMetadata={(event) => setWidth(event.currentTarget.videoWidth / 1.5)}
    />
  );
};

function isImageOnlyParagraph(node: Element | undefined): boolean {
  if (!node) return false;
  const meaningful = node.children.filter(
    (child) => !(child.type === 'text' && child.value.trim() === ''),
  );
  return meaningful.length === 1 && meaningful[0].type === 'element' && meaningful[0].tagName === 'img';
}

export const GuideArticle: React.FC<GuideArticleProps> = ({
  markdown,
  title,
  section,
  guideTitle,
  updatedAt,
  tocOpen,
  scrollRootRef,
  activeHeadingId,
  prev,
  next,
  sectionLink,
}) => {
  const outline = useMemo(() => buildGuideOutline(markdown), [markdown]);

  /* 확대 보기는 문서의 캡처 전체를 순서대로 넘길 수 있게 한 목록으로 둔다. */
  const lightboxItems = useMemo<ImageLightboxItem[]>(
    () =>
      outline.images.flatMap((image) => {
        const src = resolveGuideImage(image.src);
        return src ? [{ src, alt: image.alt, caption: image.caption, kind: isVideo(image.src) ? ('video' as const) : ('image' as const) }] : [];
      }),
    [outline.images],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const components = useMemo<Components>(() => {
    const idByText = new Map(outline.headings.map((heading) => [heading.text, heading.id]));
    const indexBySrc = new Map(lightboxItems.map((item, index) => [item.src, index]));
    return {
      h2: ({ node, children }) => {
        const text = node ? plainHeadingText(toText(node)) : '';
        const id = idByText.get(text);
        return (
          <h2 id={id}>
            {id && sectionLink && <SectionAnchor link={sectionLink(id)} label={text} />}
            {children as React.ReactNode}
          </h2>
        );
      },
      details: ({ children }) => <S.Faq>{children as React.ReactNode}</S.Faq>,
      summary: ({ children }) => (
        <S.FaqQuestion>
          <S.FaqChevron aria-hidden>
            <ChevronDown size={16} />
          </S.FaqChevron>
          <span>{children as React.ReactNode}</span>
        </S.FaqQuestion>
      ),
      p: ({ node, children }) =>
        isImageOnlyParagraph(node) ? <>{children}</> : <p>{children}</p>,
      img: ({ src, alt, title: caption }) => {
        const url = resolveGuideImage(src) ?? src;
        const size = guideMediaSize(src);
        const index = url ? indexBySrc.get(url) : undefined;
        return (
          <S.Figure>
            <S.FigureFrame
              type="button"
              aria-label={`${caption ?? alt ?? '이미지'} 크게 보기`}
              onClick={() => index !== undefined && setLightboxIndex(index)}
            >
              {/* 캡처는 2배 해상도로 찍는다. 1.5x 로 알려 주면 실제 화면보다 약 1.3배
                * 크게 그려져 글자가 잘 읽히고, 본문 폭을 넘으면 폭에 맞춰 줄어든다.
                * `src` 를 같이 주면 그쪽이 1x 후보가 되어 2배로 커지므로 srcset 만 쓴다. */}
              {url && isVideo(src) ? (
                <GuideVideo src={url} label={alt ?? caption ?? ''} size={size} />
              ) : (
                <img
                  srcSet={url ? `${url} 1.5x` : undefined}
                  alt={alt ?? ''}
                  loading="lazy"
                  /* 로드 전에도 제 크기만큼 자리를 잡는다 — 늦게 로드된 캡처가 문서를 늘리면
                   * 목차로 절에 이동하는 도중 목표가 밀려 움직임이 튄다. */
                  style={size ? { width: size[0] / 1.5, aspectRatio: `${size[0]} / ${size[1]}` } : undefined}
                />
              )}
              <S.ZoomHint aria-hidden>
                <Maximize2 size={13} />
              </S.ZoomHint>
            </S.FigureFrame>
            {caption && <S.FigureCaption>{caption}</S.FigureCaption>}
          </S.Figure>
        );
      },
      blockquote: ({ node, children }) => {
        const kind = node?.properties?.dataCallout as GuideCalloutKind | undefined;
        const { label, Icon } = CALLOUT[kind ?? 'note'];
        return (
          <S.Callout $kind={kind ?? 'note'}>
            <S.CalloutIcon $kind={kind ?? 'note'} aria-hidden>
              <Icon size={15} />
            </S.CalloutIcon>
            <S.CalloutBody>
              {kind && <S.CalloutLabel $kind={kind}>{label}</S.CalloutLabel>}
              {/* react-i18next 의 children 타입 확장이 styled 요소 자식 타입과 어긋난다. */}
              {children as React.ReactNode}
            </S.CalloutBody>
          </S.Callout>
        );
      },
      code: ({ children }) => <S.Key>{children}</S.Key>,
      /* `[MCP 실행 권한](#response-settings)` 처럼 같은 문서의 절을 가리키는 링크는
       * 주소를 바꾸지 않고 그 절로 스크롤한다. 그 밖의 링크는 새 탭으로 연다. */
      a: ({ href, children }) =>
        href?.startsWith('#') ? (
          <a
            href={href}
            onClick={(event) => {
              event.preventDefault();
              settleOnGuideHeading(scrollRootRef.current, href.slice(1));
            }}
          >
            {children as React.ReactNode}
          </a>
        ) : (
          <a href={href} target="_blank" rel="noreferrer">
            {children as React.ReactNode}
          </a>
        ),
      /* 단계 목록이 캡처를 사이에 두고 이어질 때(`3.` 부터 다시 시작) 번호를
       * 이어 붙인다. 번호 배지는 CSS 카운터라 `start` 속성을 직접 넘겨야 한다. */
      ol: ({ node, start, children }) => (
        <ol
          data-legend={isLegendList(node) || undefined}
          style={start && start > 1 ? { counterReset: `guide-step ${start - 1}` } : undefined}
        >
          {children}
        </ol>
      ),
    };
  }, [outline.headings, lightboxItems, sectionLink, scrollRootRef]);

  const scrollToHeading = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    settleOnGuideHeading(scrollRootRef.current, id);
  };

  return (
    <S.ArticleLayout>
      <S.ArticleGrid $tocOpen={tocOpen}>
        <S.Article>
          <S.Breadcrumb aria-label="위치">
            <span>{guideTitle}</span>
            {section && (
              <>
                <span aria-hidden>/</span>
                <span>{section}</span>
              </>
            )}
          </S.Breadcrumb>
          <S.Title>{title}</S.Title>
          {updatedAt && <S.Meta>{formatUpdatedAt(updatedAt)}</S.Meta>}

          {outline.intro && (
            <S.Intro>
              <ReactMarkdown
                remarkPlugins={REMARK_PLUGINS}
                rehypePlugins={INTRO_REHYPE_PLUGINS}
                components={components}
              >
                {outline.intro}
              </ReactMarkdown>
            </S.Intro>
          )}

          {outline.headings.length > 1 && (
            <S.Contents aria-label="이 문서의 목차">
              <S.ContentsTitle>이 문서에서 다루는 내용</S.ContentsTitle>
              <S.ContentsList>
                {outline.headings.map((heading) => (
                  <li key={heading.id}>
                    <a href={`#${heading.id}`} onClick={(e) => scrollToHeading(e, heading.id)}>
                      {heading.text}
                    </a>
                  </li>
                ))}
              </S.ContentsList>
            </S.Contents>
          )}

          <S.Body>
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={components}
            >
              {outline.content}
            </ReactMarkdown>
          </S.Body>

          <GuideSiblings prev={prev} next={next} />
        </S.Article>

        {outline.headings.length > 1 && (
          <S.Rail aria-label="이 페이지 내용">
            <S.RailSticky>
              <S.RailTitle>이 페이지 내용</S.RailTitle>
              <S.RailList>
                {outline.headings.map((heading) => (
                  <li key={heading.id}>
                    <S.RailLink
                      href={`#${heading.id}`}
                      $active={heading.id === activeHeadingId}
                      aria-current={heading.id === activeHeadingId ? 'location' : undefined}
                      onClick={(e) => scrollToHeading(e, heading.id)}
                    >
                      {heading.text}
                    </S.RailLink>
                  </li>
                ))}
              </S.RailList>
            </S.RailSticky>
          </S.Rail>
        )}
      </S.ArticleGrid>

      <GuideScrollTop scrollRootRef={scrollRootRef} />

      <ImageLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </S.ArticleLayout>
  );
};
